import { axiosInstance } from "@/lib/axios"
import { buildCollectionDetailPath } from "./collection-detail"
import type {
  CollectionsQueryParams,
  CollectionsResponse,
  CreateCollectionPayload,
  UploadFileResponse,
  SingleCollectionResponse,
  CollectionHoldersResponse,
  CollectionActivityResponse,
  CollectionAnalyticsResponse,
  CollectionOffersResponse,
  CollectionListingsResponse,
  CollectionTraitsResponse,
} from "./types"
import type {
  ConfirmFinanceStepPayload,
  FinanceStepResponse,
} from "@/features/finance/types"

export async function getCollections(params: CollectionsQueryParams = {}): Promise<CollectionsResponse> {
  let endpoint = "/collections";
  
  if (params.watchlist) {
    endpoint = "/collections/watchlist";
  }

  const p = new URLSearchParams()
  if (params.page) p.set("page", String(params.page))
  if (params.perPage) p.set("per_page", String(params.perPage))
  if (params.search) p.set("name", params.search)
  if (params.status) p.set("status", params.status)
  if (params.network_id) p.set("network_id", String(params.network_id))
  if (params.category_id) p.set("category_id", String(params.category_id))
  if (params.orderBy) p.set("order_by", params.orderBy)
  if (params.order) p.set("order", params.order)
  if (params.range) p.set("range", params.range)

  if (params.trend) p.set("trend", "true")
  if (params.topCollection) p.set("top-collection", "true")
  if (params.isSpecial) p.set("is_special", "1")

  const url = `${endpoint}${p.toString() ? `?${p.toString()}` : ""}`
  const { data } = await axiosInstance.get<CollectionsResponse>(url, {
    headers: params.lang ? { "lang": params.lang } : undefined,
  })
  return data
}

/**
 * ONE request for every user — it is the RESPONSE that differs.
 *
 * A custodial wallet gets an envelope with no `step` and the deploy is
 * already the backend's business. A self-custody wallet gets a `step`:
 * `SUBMITTED_TO_CHAIN` when the deployer already had gas (nothing more to
 * do), or `FUND_DEPLOYER` plus a `collection_id` and a `sign_payload` when it
 * did not — the user then sends that transaction and calls
 * `confirmCollectionFund` against the SAME id. See `./deploy-funding.ts`.
 *
 * Typed as `FinanceStepResponse` (was `any`) because that is the shape the
 * shared step machinery reads, and `any` here is what let the step marker go
 * unnoticed by every caller in the first place.
 */
export async function createCollection(
  payload: CreateCollectionPayload,
  config?: import("axios").AxiosRequestConfig
): Promise<FinanceStepResponse> {
  try {
    const { data } = await axiosInstance.post("/collections", payload, config)
    return data
  } catch (error) {
    console.error("Create collection error:", error)
    throw error
  }
}

/**
 * Closes the ONE external-wallet round the create-collection path can have:
 * the native transfer that funds the deployer.
 *
 * Posted against the `collection_id` the create response returned, never a
 * newly created one — a rejected or re-sent transaction is confirmed against
 * the collection that is already waiting, or the user ends up with two.
 *
 * Answering "not indexed yet" is EXPECTED here rather than exceptional: the
 * transfer has to be seen by the node before the backend can credit it. The
 * shared runner retries this exact call, with this exact hash, on the shared
 * retryable-error rule (`isRetryableConfirmError`) — it never sends a second
 * transaction.
 *
 * THAT RULE MATCHES ON WORDING (`not yet found/confirmed/indexed`), because the
 * API documents no machine-readable code for this condition. If this endpoint
 * ever phrases it differently the automatic retry simply stops firing — the
 * user then sees the `pending_confirmation` notice and retries by hand, which
 * re-checks the SAME hash. Degraded, never wrong; but this is the line to look
 * at if the retry appears to have stopped working.
 */
export async function confirmCollectionFund(
  collectionId: number | string,
  payload: ConfirmFinanceStepPayload
): Promise<FinanceStepResponse> {
  const { data } = await axiosInstance.post(
    `/collections/${collectionId}/confirm-fund`,
    payload
  )
  return data
}

export async function uploadFile(
  file: File,
  key: "logo" | "banner" | "attach",
  model: "member-collection" | "member-nft" | "global"
): Promise<UploadFileResponse> {
  try {
    const formData = new FormData()
    formData.append("file", file)
    formData.append("key", key)
    formData.append("model", model)

    const { data } = await axiosInstance.post<UploadFileResponse>("/upload-file", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      suppressToast: true, // Custom config to suppress default error toast
    } as any)
    return data
  } catch (error) {
    console.error("Upload file error:", error)
    throw error
  }
}

// Collection-specific like/watchlist callers used to live here. They are gone
// on purpose: `features/likes/api.ts` and `features/watchlist/api.ts` address
// every model type through the same `/like/{model}/{id}` and
// `/watchlist/{model}/{id}` routes, and having a second collections-only pair
// meant two implementations to keep in sync.

/**
 * V2 — migrated off `/collections/{contract}`. `network_id` is now required:
 * the same contract address can exist on more than one chain, and the old
 * endpoint had no way to disambiguate. Same response contract as before, see
 * `SingleCollectionResponse`.
 */
export async function getCollection(
  contractAddress: string,
  networkId: string | number,
  lang?: "fa" | "en"
): Promise<SingleCollectionResponse> {
  const { data } = await axiosInstance.get<SingleCollectionResponse>(
    buildCollectionDetailPath(contractAddress, networkId),
    { headers: lang ? { lang } : undefined }
  )
  return data
}

export async function getCollectionHolders(
  contractAddress: string,
  limit = 10,
  lang?: "fa" | "en"
): Promise<CollectionHoldersResponse> {
  const { data } = await axiosInstance.get<CollectionHoldersResponse>(
    `/collections/${contractAddress}/holders`,
    {
      params: { limit },
      headers: lang ? { lang } : undefined,
    }
  )
  return data
}

/**
 * `page` is forwarded as of Phase 05, after measuring that the endpoint honours
 * it: on `0xbc4ca0…`, page 1 ends at `2026-08-03T20:14:47Z` and page 2 begins at
 * `2026-08-03T20:02:11Z`, continuing the same descending timeline, and repeating
 * page 1 returns the identical window — so it pages deterministically.
 *
 * ITS `page` META IS NOT USABLE, THOUGH. Every response reports
 * `{last_page: 1, total: 20, per_page: 20}` regardless of the page requested, so
 * `last_page` describes the window rather than the collection. The caller must
 * derive "there may be more" from a full page of rows, not from `last_page` —
 * see `ActivityTab`. Recorded as a backend dependency, not worked around
 * silently.
 */
export async function getCollectionActivity(
  contractAddress: string,
  lang?: "fa" | "en",
  page?: number
): Promise<CollectionActivityResponse> {
  const { data } = await axiosInstance.get<CollectionActivityResponse>(
    `/collections/${contractAddress}/activity`,
    {
      params: page && page > 1 ? { page } : undefined,
      headers: lang ? { lang } : undefined,
    }
  )
  return data
}

export async function getCollectionAnalytics(
  contractAddress: string,
  lang?: "fa" | "en"
): Promise<CollectionAnalyticsResponse> {
  const { data } = await axiosInstance.get<CollectionAnalyticsResponse>(
    `/collections/${contractAddress}/analytics`,
    { headers: lang ? { lang } : undefined }
  )
  return data
}

/**
 * V2 — migrated off `/collections/{contract}/offers` (Phase 11 finance
 * hotfix). Measured live: both paths return the identical on-chain
 * marketplace-bid shape and the same `last_page = current_page - 1` bug
 * (see `offers-pagination.ts`), so this is a same-shape endpoint swap, not a
 * response-shape change.
 */
export async function getCollectionOffers(
  contractAddress: string,
  params?: { limit?: number; page?: number },
  lang?: "fa" | "en"
): Promise<CollectionOffersResponse> {
  const { data } = await axiosInstance.get<CollectionOffersResponse>(
    `/collections/V2/${contractAddress}/offers`,
    {
      params: { limit: params?.limit, page: params?.page },
      headers: lang ? { lang } : undefined,
    }
  )
  return data
}

/** The collection's open sale listings, newest first (no auth, never cached for long). */
export async function getCollectionListings(
  contractAddress: string,
  params?: { limit?: number; page?: number },
  lang?: "fa" | "en"
): Promise<CollectionListingsResponse> {
  const { data } = await axiosInstance.get<CollectionListingsResponse>(
    `/collections/${contractAddress}/listings`,
    {
      params: { limit: params?.limit, page: params?.page },
      headers: lang ? { lang } : undefined,
    }
  )
  return data
}

/** Every trait of the collection with its values and counts. `network_id` only when the address alone is ambiguous. */
export async function getCollectionTraits(contractAddress: string, networkId?: number | null): Promise<CollectionTraitsResponse> {
  const { data } = await axiosInstance.get<CollectionTraitsResponse>(
    `/collections/${contractAddress}/traits`,
    { params: networkId ? { network_id: networkId } : undefined }
  )
  return data
}
